import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type SelectHTMLAttributes,
  useId,
} from 'react';

import { cn } from '../../utils';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeStyles = {
  sm: 'h-10',
  md: 'h-12',
  lg: 'h-14',
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      hint,
      options,
      placeholder,
      size = 'md',
      className,
      id,
      disabled,
      onFocus,
      onBlur,
      // Se extrae como `onFocus`/`onBlur`: si viajara dentro de `...props`
      // sobrescribiría el handler interno, porque el spread va al final.
      onChange,
      ...props
    },
    ref
  ) => {
    const reactId = useId();
    const selectId = id || `select-${reactId}`;
    const hasError = Boolean(error);
    // `subtle` es el color del placeholder y sobre blanco da 2.75:1 — por
    // debajo del mínimo AA (4.5:1). Sólo vale cuando el select está mostrando
    // DE VERDAD el placeholder.
    //
    // Saberlo por props no alcanza: `register()` de react-hook-form deja el
    // select NO controlado, así que no llegan ni `value` ni `defaultValue`
    // aunque haya una opción real elegida. Mirando sólo las props, esos
    // selects se pintaban siempre apagados —"País" se veía atenuado al lado de
    // "Nacionalidad fiscal", que sí es controlado, con la misma opción
    // elegida—.
    //
    // Se lee el valor REAL del elemento. Cubre los tres casos: controlado,
    // no controlado, y el valor puesto por código (`reset()`/`setValue()` al
    // rehidratar un step), que no dispara `onChange`.
    const innerRef = useRef<HTMLSelectElement | null>(null);
    const [domValue, setDomValue] = useState<string | null>(null);

    const setRefs = useCallback(
      (node: HTMLSelectElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    // Sin arreglo de dependencias: corre tras cada render y sincroniza si el
    // DOM cambió por fuera de React. La regla avisa de un bucle de
    // actualizaciones; aquí no lo hay porque sólo se llama a `setDomValue`
    // cuando el valor del elemento DIFIERE del estado, así que el render que
    // provoca ya no vuelve a entrar. Con `[domValue]` no serviría: los cambios
    // que interesan (`reset()`/`setValue()`) no pasan por el estado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      const node = innerRef.current;
      if (node && node.value !== domValue) setDomValue(node.value);
    });

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      // Un select no controlado no re-renderea al elegir opción, así que el
      // efecto de arriba no correría: se actualiza aquí para que el color
      // cambie en el mismo gesto.
      setDomValue(e.target.value);
      onChange?.(e);
    };

    const showsPlaceholder = Boolean(placeholder) && (domValue ?? '') === '';
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    return (
      <div className="w-full">
        <div className="relative">
          {/* Floating Label - Always positioned above border */}
          {label && (
            <label
              htmlFor={selectId}
              // La muesca del borde se pinta SIEMPRE con `--tkf-surface`, aunque
              // el campo esté deshabilitado. Con `--tkf-surface-muted` la mitad
              // inferior del degradado no casaba con el fondo de la card y
              // dejaba una costura horizontal a media altura del texto: la
              // etiqueta se leía partida por la mitad, no inhabilitada.
              style={{
                background: 'linear-gradient(to bottom, transparent 45%, var(--tkf-surface) 45%)',
              }}
              className={cn(
                'absolute left-3 top-0 -translate-y-1/2 pointer-events-none',
                // `whitespace-nowrap`: la muesca del borde se finge con un
                // degradado que asume UNA línea (transparente arriba, superficie
                // abajo). Si la etiqueta hace wrap, la segunda línea cae sobre el
                // campo con fondo transparente y se encima con el valor —lo que
                // pasaba con "Frecuencia de operaciones (estimado promedio
                // mensual)" sobre "Baja"—. Una etiqueta flotante que envuelve es
                // siempre un bug en este diseño: se acorta el texto, no se apila.
                // `max-w-full truncate`: red de seguridad para etiquetas más
                // anchas que el campo. Con sólo `nowrap` se desbordarían; así
                // cortan con elipsis. No afecta a lectores de pantalla —el
                // texto completo sigue en el DOM—, pero si una etiqueta llega a
                // truncarse visualmente, lo que hay que arreglar es el texto.
                'max-w-full truncate whitespace-nowrap px-1 text-sm font-medium transition-colors duration-200',
                // La etiqueta NO se atenúa al deshabilitar: `--tkf-fg-disabled`
                // es 30% de opacidad y dejaba el texto por debajo de AA —se leía
                // como un campo roto, no como uno inhabilitado—. Que el campo
                // está deshabilitado ya lo dicen su fondo, el color del valor y
                // el cursor; el nombre del dato tiene que seguir siendo legible.
                hasError
                  ? 'text-tkf-fg-error'
                  : isFocused
                    ? 'text-primary-500'
                    : 'text-tkf-fg-muted'
              )}
            >
              {label}
            </label>
          )}

          <select
            ref={setRefs}
            id={selectId}
            className={cn(
              'block w-full appearance-none rounded-lg border bg-tkf-surface px-4 pr-11 transition-colors duration-200',
              showsPlaceholder ? 'text-tkf-fg-subtle' : 'text-tkf-fg',
              'focus:outline-none focus:ring-1',
              'disabled:cursor-not-allowed disabled:bg-tkf-surface-muted disabled:text-tkf-fg-disabled',
              hasError
                ? 'border-tkf-line-error focus:border-tkf-line-error focus:ring-tkf-line-error'
                : 'border-tkf-line-medium focus:border-primary-500 focus:ring-primary-500',
              sizeStyles[size],
              className
            )}
            disabled={disabled}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            aria-invalid={hasError}
            aria-describedby={
              hasError ? `${selectId}-error` : hint ? `${selectId}-hint` : undefined
            }
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map(option => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>

          <svg
            className={cn(
              'pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2',
              hasError ? 'text-tkf-fg-error' : 'text-tkf-fg'
            )}
            fill="none"
            viewBox="0 0 20 20"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 8l4 4 4-4" />
          </svg>
        </div>

        {error && (
          <p id={`${selectId}-error`} className="mt-1 text-sm text-tkf-fg-error">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${selectId}-hint`} className="mt-1 text-sm text-tkf-fg-muted">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
